/**
 * Hash Generator Tool
 * 
 * Generates cryptographic hashes using Web Crypto API.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const HashGeneratorTool: ITransformerTool = {
  id: 'hash-generator',
  name: 'Hash Generator',
  description: 'Generate SHA-1, SHA-256, SHA-384, or SHA-512 hashes',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['hash', 'sha', 'sha256', 'sha512', 'checksum', 'crypto'],

  seo: {
    slug: 'hash-generator',
    pageTitle: 'Hash Generator - SHA-1, SHA-256, SHA-512 Online',
    metaDescription: 'Generate SHA-1, SHA-256, SHA-384, and SHA-512 hashes from any text. Compare checksums instantly. Fast, private, runs entirely in your browser.',
    keywords: ['hash generator', 'sha256 hash', 'sha512 hash', 'checksum calculator'],
    relatedTools: ['password-generator'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.HASH,

  attributes: [
    {
      name: 'algorithm',
      type: 'select',
      label: 'Algorithm',
      defaultValue: 'SHA-256',
      options: ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'],
      description: 'Hash algorithm to use',
    },
    {
      name: 'uppercase',
      type: 'boolean',
      label: 'Uppercase',
      defaultValue: false,
      description: 'Output hash in uppercase',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const algorithm = (config.algorithm as string) || 'SHA-256';
    const uppercase = config.uppercase as boolean;

    // Encode the string as bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(input);

    // Generate hash using Web Crypto API
    const hashBuffer = await crypto.subtle.digest(algorithm, data);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    let hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    if (uppercase) {
      hashHex = hashHex.toUpperCase();
    }

    return hashHex;
  },
};

